import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Group, Menu, SegmentedControl, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, costsApi, referenceApi, type Benchmark, type CostFlag, type CostGroup, type CostItem, type CostItemInput, type CostPlan, type CostStage, undoPaths } from '../../api';
import { COST_FLAGS, COST_STAGES, formatAmount, formatEuro, formatQuantity } from '../../format';
import { EditableText } from '../../ui/EditableText';
import { useContextMenu } from '../../ui/ContextMenu';
import { FormulaCell } from '../../ui/FormulaCell';
import { Section } from '../../ui/Section';
import { BenchmarkPicker } from './BenchmarkPicker';
import { convertBenchmark, matchDistrict } from './conversion';
import { TakeoffModal } from './TakeoffModal';
import { inVariant, kgFunctions, resultFor, variablesOf } from './variant';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';

const FLAG_ORDER: (CostFlag | null)[] = [null, 'A', '?', '!'];
const FLAG_ROW: Record<CostFlag, string | undefined> = {
  A: undefined,
  '?': 'var(--mantine-color-yellow-light)',
  '!': 'var(--mantine-color-red-light)',
};

/**
 * The cost estimate of one cost group in one stage, in one variant: lines of
 * quantity × rate. Both may be formulas over the plan's values; the quantity
 * may instead come from a take-off of several rows. A line either belongs to
 * every variant or only to one.
 */
export function CostEstimatePanel({
  projectId,
  plan,
  group,
  stage,
  variantId,
  catalog,
  onStage,
  onClose,
  canEdit,
}: {
  projectId: string;
  plan: CostPlan;
  group: CostGroup;
  stage: CostStage;
  variantId: string;
  /** Whether this user may use the benchmark catalog. */
  catalog: boolean;
  onStage: (stage: CostStage) => void;
  onClose: () => void;
  canEdit: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  const add = useMutation({ mutationFn: (body: CostItemInput) => costsApi.addItem(projectId, group.id, stage, body), onSettled: refresh });
  const undo = useUndo();
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CostItemInput) => {
      const before = plan.items.find((i) => i.id === id);
      const step = before
        ? guardedStep(`Position ${before.description.slice(0, 40)}`, 'PATCH', undoPaths.costItem(projectId, id), inversePatch(body, before as unknown as Record<string, unknown>), body)
        : null;
      return undo.recordAfter(costsApi.updateItem(projectId, id, body), step);
    },
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => costsApi.removeItem(projectId, id), onSettled: refresh });
  // For converting a catalog value (AP-80): factor from the location, index of the plan's building type.
  const factors = useQuery({ queryKey: ['regional-factors'], queryFn: referenceApi.factors, enabled: catalog });
  const indices = useQuery({ queryKey: ['price-indices'], queryFn: referenceApi.indices });
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const takeBenchmark = useMutation({
    mutationFn: async ({ item, b, value, which }: { item: CostItem; b: Benchmark; value: number; which: string }) => {
      const location = projects.data?.find((p) => p.id === projectId)?.location ?? null;
      const conv = convertBenchmark({
        value, benchmark: b, planValues: plan.values, factor: matchDistrict(location, factors.data ?? []).match,
        indices: indices.data ?? [], indexType: plan.priceIndexType,
      });
      for (const v of conv.create) await costsApi.addValue(projectId, { name: v.name, formula: v.formula, label: v.label, kind: 'rate' });
      const origin = [b.source, b.edition, b.priceDate, b.region, b.buildingType].filter(Boolean).join(', ') + ` – ${b.costGroup ? `KG ${b.costGroup} ` : ''}${b.label} (${which})`;
      await new Promise<void>((resolve, reject) => update.mutate({
        id: item.id,
        unitPriceFormula: conv.formula,
        unit: b.unit?.split('/').pop()?.trim() || item.unit,
        benchmarkId: b.id,
        source: [origin, ...conv.notes].join('; '),
        // The catalogue's long text says what the rate covers; kept with the line unless it has a note already.
        ...(b.description && !item.note ? { note: [b.description, b.duration ? `Ausführungsdauer: ${b.duration}` : ''].filter(Boolean).join('\n') } : {}),
      }, { onSuccess: () => resolve(), onError: reject }));
    },
    onSettled: refresh,
  });
  const copy = useMutation({ mutationFn: (from: CostStage) => costsApi.copyStage(projectId, from, stage), onSettled: refresh });
  const [description, setDescription] = useState('');
  const [takeoff, setTakeoff] = useState<CostItem | null>(null);
  const [picking, setPicking] = useState<CostItem | null>(null);
  // Opened from a row far up the table: bring it into view instead of making the user look for it.
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [group.id]);
  const error = add.error ?? update.error ?? remove.error ?? copy.error ?? takeBenchmark.error;
  // Right-click on a line: clear its quantity or rate, or remove it - the
  // same as the cells and ✕ do, so nothing depends on the right mouse button.
  const lineMenu = useContextMenu<CostItem>((l, close) => {
    const run = (f: () => void) => () => { close(); f(); };
    const hasQuantity = !!l.quantityFormula || !!l.takeoff?.length;
    return (
      <>
        <Menu.Label title={l.description}>{l.description.length > 40 ? `${l.description.slice(0, 40)} …` : l.description || 'Position'}</Menu.Label>
        <Menu.Item onClick={run(() => setTakeoff(l))}>Mengenermittlung …</Menu.Item>
        {catalog && <Menu.Item onClick={run(() => setPicking(l))}>Kennwert aus dem Katalog …</Menu.Item>}
        <Menu.Divider />
        <Menu.Item disabled={!hasQuantity} onClick={run(() => update.mutate({ id: l.id, quantityFormula: null, takeoff: null }))}>Menge löschen</Menu.Item>
        <Menu.Item disabled={!l.unitPriceFormula} onClick={run(() => update.mutate({ id: l.id, unitPriceFormula: null, benchmarkId: null }))}>Kennwert löschen</Menu.Item>
        <Menu.Divider />
        <Menu.Item color="red" onClick={run(() => { if (window.confirm(`Position „${l.description || 'ohne Bezeichnung'}“ löschen?`)) remove.mutate(l.id); })}>Position löschen …</Menu.Item>
      </>
    );
  });

  const result = resultFor(plan, variantId);
  const vars = variablesOf(result, stage);
  const fns = kgFunctions(result, stage);
  const variant = plan.variants.find((v) => v.id === variantId);
  const lines = plan.items.filter((i) => i.groupId === group.id && i.stage === stage && inVariant(i, variantId));
  const hidden = plan.items.filter((i) => i.groupId === group.id && i.stage === stage && !inVariant(i, variantId)).length;
  const total = lines.reduce((s, l) => s + (result.lines[l.id]?.amount ?? 0), 0);
  const stageLabel = COST_STAGES.find((s) => s.key === stage)!.label;
  const counts = new Map(COST_STAGES.map((s) => [s.key, plan.items.filter((i) => i.groupId === group.id && i.stage === s.key && inVariant(i, variantId)).length]));
  const open = lines.filter((l) => l.flag === '?').length;

  return (
    <div ref={anchor} style={{ scrollMarginTop: 8 }}>
    <Section
      title={`Kostenermittlung KG ${group.code} ${group.name}${variant ? ` · ${variant.code}` : ''}`}
      description={`${stageLabel}: ${lines.length} ${lines.length === 1 ? 'Position' : 'Positionen'}, ${formatEuro(Math.round(total * 100) / 100)} netto${open ? `, ${open} offen` : ''}. Die Summe der Positionen zählt zur Kostengruppe, zusammen mit ihren Untergruppen.`}
      actions={
        <>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target><Button size="xs" variant="default" disabled={!canEdit}>Aus Stufe übernehmen</Button></Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Positionen kopieren nach „{stageLabel}“</Menu.Label>
              {COST_STAGES.filter((s) => s.key !== stage).map((s) => (
                <Menu.Item key={s.key} disabled={!plan.items.some((i) => i.stage === s.key)} onClick={() => copy.mutate(s.key)}>
                  aus {s.label}
                </Menu.Item>
              ))}
              <Menu.Divider />
              <Menu.Label>Gilt für alle Kostengruppen, die in dieser Stufe noch keine Positionen haben.</Menu.Label>
            </Menu.Dropdown>
          </Menu>
          <Button size="xs" variant="default" component="a" href={costsApi.stageCsvUrl(projectId, stage, variantId || undefined)}>{stageLabel} als CSV</Button>
          <Button size="xs" variant="subtle" color="gray" onClick={onClose}>Schließen</Button>
        </>
      }
    >
      <SegmentedControl
        size="xs"
        value={stage}
        onChange={(v) => onStage(v as CostStage)}
        data={COST_STAGES.map((s) => ({ value: s.key, label: `${s.short}${counts.get(s.key) ? ` (${counts.get(s.key)})` : ''}` }))}
      />
      {copy.isSuccess && <Text size="xs" c="dimmed">{copy.data.copied} Positionen übernommen.</Text>}
      {error && <Alert color="red" py={6} withCloseButton onClose={() => [add, update, remove, copy].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}

      <Table.ScrollContainer minWidth={900}>
      <Table verticalSpacing={3} className="pst-tabular">
        <Table.Thead>
          <Table.Tr>
            {canEdit && <Table.Th w={30} />}
            <Table.Th w={34} title="Kennzeichen: A Annahme, ? offen, ! Vorsicht – anklicken wechselt">Kz.</Table.Th>
            <Table.Th>Position</Table.Th>
            {plan.variants.length > 0 && <Table.Th w={70} title="In allen Varianten oder nur in einer">gilt</Table.Th>}
            <Table.Th w={150} ta="right" title="Menge oder Formel, z. B. 2*(12,5+8)*3 oder BGF*0,3">Menge</Table.Th>
            <Table.Th w={60}>Einheit</Table.Th>
            <Table.Th w={140} ta="right" title="Zahl oder Formel, z. B. 450, KW_330 oder KG(300)*5%">Kennwert (€/E)</Table.Th>
            <Table.Th w={120} ta="right">Betrag (€)</Table.Th>
            <Table.Th w={180}>Quelle</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {lines.map((l) => {
            const r = result.lines[l.id];
            const scope = plan.variants.find((v) => v.id === l.variantId);
            const nextFlag = FLAG_ORDER[(FLAG_ORDER.indexOf(l.flag) + 1) % FLAG_ORDER.length]!;
            return (
              <Table.Tr key={l.id} style={l.flag ? { background: FLAG_ROW[l.flag] } : undefined} onContextMenu={canEdit ? (e) => lineMenu.open(e, l) : undefined}>
                {canEdit && <Table.Td><ActionIcon size="xs" variant="subtle" color="gray" aria-label="Position löschen" title="Position löschen" onClick={() => remove.mutate(l.id)}>✕</ActionIcon></Table.Td>}
                <Table.Td>
                  <Tooltip label={l.flag ? `${COST_FLAGS[l.flag].label}: ${COST_FLAGS[l.flag].hint}` : 'ohne Kennzeichen – anklicken für A, ?, !'}>
                    <Badge
                      size="sm" variant={l.flag ? 'light' : 'outline'} color={l.flag ? COST_FLAGS[l.flag].color : 'gray'}
                      style={{ cursor: canEdit ? 'pointer' : undefined, opacity: l.flag ? 1 : 0.35, minWidth: 24 }}
                      onClick={() => canEdit && update.mutate({ id: l.id, flag: nextFlag })}
                    >{l.flag ?? '·'}</Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td><EditableText value={l.description} onCommit={(d) => canEdit && update.mutate({ id: l.id, description: d })} /></Table.Td>
                {plan.variants.length > 0 && (
                  <Table.Td>
                    <Menu withinPortal position="bottom-start" disabled={!canEdit}>
                      <Menu.Target>
                        <Badge size="sm" variant={scope ? 'filled' : 'default'} style={{ cursor: canEdit ? 'pointer' : undefined }}>{scope ? scope.code : 'alle'}</Badge>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => update.mutate({ id: l.id, variantId: null })}>in allen Varianten</Menu.Item>
                        {plan.variants.map((v) => <Menu.Item key={v.id} onClick={() => update.mutate({ id: l.id, variantId: v.id })}>nur in {v.code} {v.name}</Menu.Item>)}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                )}
                <Table.Td>
                  <Group gap={2} wrap="nowrap" justify="flex-end">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {l.takeoff && l.takeoff.length > 0 ? (
                        <Tooltip label={`Mengenermittlung, ${l.takeoff.length} Zeilen – anklicken`}>
                          <Text size="sm" ta="right" c={r?.error ? 'red.7' : undefined} className="pst-cell" role="button" tabIndex={0} onClick={() => setTakeoff(l)} onKeyDown={(e) => e.key === 'Enter' && setTakeoff(l)}>
                            {r?.error ? 'Fehler' : formatQuantity(r?.quantity ?? null)}<Text span size="xs" c="dimmed"> Σ</Text>
                          </Text>
                        </Tooltip>
                      ) : (
                        <FormulaCell formula={l.quantityFormula} value={r?.quantity ?? null} variables={vars} functions={fns} error={r?.error?.startsWith('Menge') ? r.error : null} readOnly={!canEdit} onCommit={(quantityFormula) => update.mutate({ id: l.id, quantityFormula })} />
                      )}
                    </div>
                    {canEdit && !(l.takeoff && l.takeoff.length > 0) && (
                      <Tooltip label="Mengenermittlung in mehreren Zeilen">
                        <ActionIcon size="xs" variant="subtle" color="gray" aria-label="Mengenermittlung" onClick={() => setTakeoff(l)}>Σ</ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td><EditableText value={l.unit ?? ''} allowEmpty placeholder=" " onCommit={(unit) => canEdit && update.mutate({ id: l.id, unit: unit || null })} /></Table.Td>
                <Table.Td>
                  <Group gap={2} wrap="nowrap" justify="flex-end">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <FormulaCell
                        formula={l.unitPriceFormula} value={r?.unitPrice ?? null} variables={vars} functions={fns} money empty=""
                        emptyHint="Zahl oder Formel" placeholder="z. B. 450 oder KW_330"
                        error={r?.error?.startsWith('Kennwert') || r?.error?.startsWith('Zirkel') ? r.error : null}
                        readOnly={!canEdit} onCommit={(unitPriceFormula) => update.mutate({ id: l.id, unitPriceFormula })}
                      />
                    </div>
                    {canEdit && catalog && (
                      <Tooltip label="Kennwert aus dem Katalog übernehmen (von, Mittel oder bis)">
                        <Button size="compact-xs" variant="light" leftSection={<span aria-hidden>⌕</span>} aria-label="Kennwert aus dem Katalog" onClick={() => setPicking(l)} style={{ flexShrink: 0 }}>Katalog</Button>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={700} ta="right" c={r?.error ? 'red.7' : r?.amount === null ? 'dimmed' : undefined} title={r?.error ?? undefined}>
                    {r?.error ? 'Fehler' : r?.amount === null || r?.amount === undefined ? '—' : formatAmount(r.amount)}
                  </Text>
                </Table.Td>
                <Table.Td><EditableText value={l.source ?? ''} size="xs" dimmed allowEmpty placeholder="z. B. Erfahrung, Angebot" onCommit={(source) => canEdit && update.mutate({ id: l.id, source: source || null })} /></Table.Td>
              </Table.Tr>
            );
          })}
          {lines.length > 0 && (
            <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
              {canEdit && <Table.Td />}
              <Table.Td />
              <Table.Td colSpan={plan.variants.length > 0 ? 5 : 4}><Text size="sm" fw={700}>Summe der Positionen KG {group.code}, {stageLabel}</Text></Table.Td>
              <Table.Td><Text size="sm" fw={700} ta="right">{formatAmount(Math.round(total * 100) / 100)}</Text></Table.Td>
              <Table.Td />
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      {lines.length === 0 && <Text size="sm" c="dimmed">Noch keine Positionen in dieser Stufe.</Text>}
      {hidden > 0 && <Text size="xs" c="dimmed">{hidden} weitere {hidden === 1 ? 'Position gilt' : 'Positionen gelten'} nur in einer anderen Variante.</Text>}
      {canEdit && (
        <form onSubmit={(e) => { e.preventDefault(); add.mutate({ description: description.trim() || 'Position', unit: 'm²' }, { onSuccess: () => setDescription('') }); }}>
          <Group gap="xs" align="flex-end">
            <TextInput size="xs" placeholder="z. B. Außenwand Mauerwerk 36,5 cm" value={description} onChange={(e) => setDescription(e.currentTarget.value)} style={{ flex: 1, maxWidth: 420 }} aria-label="Neue Position" />
            <Button size="xs" type="submit" variant="light" loading={add.isPending}>+ Position</Button>
          </Group>
        </form>
      )}
      <Text size="xs" c="dimmed">
        Menge und Kennwert anklicken: Zahl oder Formel mit + − × / % und Klammern, mit Namen aus „Mengen, Kennwerte und Kennzahlen“ und KG(300) für die Summe einer Kostengruppe. Ohne Menge gilt der Kennwert als Pauschalbetrag.
      </Text>
    </Section>
    {takeoff && (
      <TakeoffModal
        item={takeoff}
        variables={vars}
        functions={fns}
        onClose={() => setTakeoff(null)}
        onSave={(rows) => update.mutate({ id: takeoff.id, takeoff: rows }, { onSuccess: () => setTakeoff(null) })}
      />
    )}
    {picking && (
      <BenchmarkPicker
        costGroup={group.code}
        unit={picking.unit}
        onClose={() => setPicking(null)}
        onPick={(b, value, which) => takeBenchmark.mutate({ item: picking, b, value, which }, { onSuccess: () => setPicking(null) })}
      />
    )}
    {lineMenu.menu}
    </div>
  );
}
